import React, { useCallback, useMemo, useState, useEffect } from 'react';
import trans from 'translate';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { createTokenLink } from 'services/utilsService';
import TokenWithNetwork from '../TokenWithNetwork/TokenWithNetwork';
import ChevronDownIcon from '../Icons/ChevronDownIcon';

import './TokensPercentagesFullList.scss';
import { generateColor } from '../../../utils/colors';
import { getValueByMultiProps } from '../../../utils/mapper';
import { displayFloats } from '../../../utils/numbersDisplay';

const TokensPercentagesFullList = ({ tokens, renderPercentage, scan }) => {
  const [showAll, setShowAll] = useState(false);
  const [tokensToShow, setTokensToShow] = useState([]);

  const getMultiProp = (token, key) => getValueByMultiProps(token, [`token_${key}`, key]);

  const getAddress = (token) => getMultiProp(token, 'address');
  const getName = (token) => getMultiProp(token, 'name');

  useEffect(() => {
    if (!showAll) tokens = [...tokens].slice(0, 5);

    setTokensToShow(
      tokens.map((token) => ({
        ...token,
        color: generateColor(getName(token)),
        link: createTokenLink(getAddress(token), token.network, scan),
        name: getValueByMultiProps(token, ['token_name', 'name']),
        percentage: getValueByMultiProps(token, ['token_ratio', 'percentage']),
      })),
    );
  }, [tokens, showAll, scan]);

  const showShowAllButton = useMemo(() => tokens.length > 5, [tokens]);
  const setShowAllCallback = useCallback(() => setShowAll(!showAll), [showAll, setShowAll]);

  const tokenPercentage = (token) =>
    displayFloats(getValueByMultiProps(token, ['percentage', 'current_ratio_from_pool', 'token_ratio']) || 0);

  return (
    <div className="tokens-percentages-full-list-container">
      <div className="tokens-percentages-full-list-wrapper">
        {tokensToShow.map((token) =>
          token.percentage === 0 && token.name === 'Tether' ? null : (
            <div key={token.token_id} className="tokens-percentages-single-token-wrapper">
              <div className="token-name-symbol-wrapper">
                <div className="token-icon-name-wrapper">
                  <TokenWithNetwork
                    token={{
                      ...token,
                      token_id: token.token_id,
                      network: 'binance',
                    }}
                  />
                  <div className="name-wrapper font-lg">{getMultiProp(token, 'name')}</div>
                </div>

                <div className="token-link-wrapper font-lg">
                  {token.link ? (
                    <a target="_blank" rel="noopener noreferrer" href={token.link} className="explorer-link">
                      {getMultiProp(token, 'symbol')}
                    </a>
                  ) : (
                    <div className="explorer-symbol">{getMultiProp(token, 'symbol')}</div>
                  )}
                </div>
              </div>
              <div className="token-percentage-and-bar-wrapper">
                <div className="token-percentage-bar-wrapper">
                  <div
                    className="token-percentage-bar-fill"
                    style={{
                      width: `${tokenPercentage(token)}%`,
                      backgroundColor: getMultiProp(token, 'color'),
                    }}
                  />
                </div>

                <div className="token-percentage-wrapper">
                  {renderPercentage ? renderPercentage?.(token) : `${tokenPercentage(token)}%`}
                </div>
              </div>
            </div>
          ),
        )}
      </div>

      {showShowAllButton && (
        <div className="show-all-button-container">
          <div className={clsx('show-all-button-wrapper', { shown: showAll })} onClick={setShowAllCallback}>
            {showAll
              ? trans('pool.tokens_percentages_full_list_show_less')
              : trans('pool.tokens_percentages_full_list_show_all')}

            <ChevronDownIcon />
          </div>
        </div>
      )}
    </div>
  );
};

TokensPercentagesFullList.defaultProps = {
  renderPercentage: null,
};

TokensPercentagesFullList.propTypes = {
  tokens: PropTypes.array.isRequired,
  renderPercentage: PropTypes.func,
  scan: PropTypes.string.isRequired,
};

const mapStateToProps = ({ general }) => ({
  // TODO remove the link value when BE send scan url at staging
  scan: general.scan || 'https://testnet.bscscan.com/',
});

export default connect(mapStateToProps)(TokensPercentagesFullList);
